import React, { useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * Tutarı TL formatında gösterir
 */
const formatCurrency = (value, currency = 'TRY') => {
    if (value == null) return '-';
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    }).format(value);
};

/**
 * Tarihi formatlar
 */
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Durum badge rengi
 * 1 -> Beklemede (warning)
 * 2 -> Başarılı (success)
 * 3 -> Başarısız (danger)
 */
const getStatusSeverity = (status) => {
    switch (status) {
        case 2:
            return 'success';
        case 3:
            return 'danger';
        case 1:
            return 'warning';
        default:
            return 'info';
    }
};

/**
 * Durum metni
 */
const getStatusText = (status, statusName) => {
    // Backend'den statusName geliyorsa onu kullan
    if (statusName) return statusName;
    
    // Yoksa status koduna göre belirle
    switch (status) {
        case 1:
            return 'Beklemede';
        case 2:
            return 'Başarılı';
        case 3:
            return 'Başarısız';
        default:
            return 'Bilinmiyor';
    }
};

const PaymentReportsTable = ({
    rows = [],
    loading = false,
    totalCount = 0,
    pagination = { PageNumber: 1, PageSize: 10 },
    sortField,
    sortOrder,
    onPageChange,
    onSort,
    onDownloadReceipt,
    isLoadingReceipt
}) => {
    const { th } = useLanguage();

    // Bayi Kodu kolonu
    const dealerCodeTemplate = (rowData) => {
        return <span className="font-medium">{rowData.dealerCode || '-'}</span>;
    };

    // Bayi Adı kolonu
    const dealerNameTemplate = (rowData) => {
        return <span>{rowData.dealerName || '-'}</span>;
    };

    // Banka kolonu
    const bankNameTemplate = (rowData) => {
        return <span>{rowData.bankName || '-'}</span>;
    };

    // Tutar kolonu
    const amountTemplate = (rowData) => {
        return (
            <span className="font-semibold text-green-600">
                {formatCurrency(rowData.amount, rowData.currency)}
            </span>
        );
    };

    // Ödeme Tipi kolonu (paymentTypeName)
    const paymentTypeTemplate = (rowData) => {
        return <span>{rowData.paymentTypeName || '-'}</span>;
    };

    // Taksit kolonu (installmentLabel veya installmentCount)
    const installmentTemplate = (rowData) => {
        if (rowData.installmentLabel) {
            return <span>{rowData.installmentLabel}</span>;
        }
        if (rowData.installmentCount > 1) {
            return <span>{rowData.installmentCount} {th('paymentReports.columns.installmentSuffix')}</span>;
        }
        return <span>{th('paymentReports.columns.singlePayment')}</span>;
    };

    // Tarih kolonu 
    const dateTemplate = (rowData) => {
        return <span>{formatDate(rowData.createdAt || rowData.transactionDate)}</span>;
    };

    // Durum kolonu
    const statusTemplate = (rowData) => {
        const statusText = getStatusText(rowData.status, rowData.statusName);
        return (
            <Badge 
                value={statusText} 
                severity={getStatusSeverity(rowData.status)}
            />
        );
    };

    // İşlemler kolonu - sadece başarılı ödemelerde (status === 2) makbuz indir göster
    const actionsTemplate = useCallback((rowData) => {
        // Başarılı olmayan ödemeler için boş döndür
        if (rowData.status !== 2) {
            return null;
        }

        return (
            <Button
                icon="pi pi-download"
                className="p-button-rounded p-button-text p-button-sm"
                tooltip={th('paymentReports.actions.downloadReceipt') || 'Makbuz İndir'}
                tooltipOptions={{ position: 'top' }}
                onClick={() => onDownloadReceipt(rowData.transactionId, rowData)}
                loading={isLoadingReceipt}
                disabled={!rowData.transactionId}
            />
        );
    }, [th, onDownloadReceipt, isLoadingReceipt]);

    return (
        <DataTable
            value={rows}
            dataKey="transactionId"
            lazy
            paginator
            first={(pagination.PageNumber - 1) * pagination.PageSize}
            rows={pagination.PageSize}
            totalRecords={totalCount}
            onPage={onPageChange}
            onSort={onSort}
            sortField={sortField}
            sortOrder={sortOrder}
            loading={loading}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            tableStyle={{ minWidth: '70rem' }}
            showGridlines
            stripedRows
            sortMode="single"
            emptyMessage={th('paymentReports.empty')}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate={th('table.paginatorTemplate')}
        >
            <Column 
                header={th('common.actions')} 
                body={actionsTemplate}
                exportable={false}
                style={{ width: '5%', textAlign: 'center' }}
            />
            <Column 
                field="dealerCode" 
                header={th('paymentReports.columns.dealerCode')} 
                body={dealerCodeTemplate}
                sortable 
                style={{ width: '10%' }}
            />
            <Column 
                field="dealerName" 
                header={th('paymentReports.columns.dealerName')} 
                body={dealerNameTemplate}
                sortable 
                style={{ width: '17%' }}
            />
            <Column 
                field="bankName" 
                header={th('paymentReports.columns.bankName')} 
                body={bankNameTemplate}
                sortable 
                style={{ width: '10%' }}
            />
            <Column 
                field="paymentTypeName" 
                header={th('paymentReports.columns.paymentType')} 
                body={paymentTypeTemplate}
                sortable 
                style={{ width: '10%' }}
            />
            <Column 
                field="installmentCount" 
                header={th('paymentReports.columns.installment')} 
                body={installmentTemplate}
                sortable 
                style={{ width: '9%' }}
            />
            <Column 
                field="amount" 
                header={th('paymentReports.columns.amount')} 
                body={amountTemplate}
                sortable 
                style={{ width: '11%' }}
            />
            <Column 
                field="createdAt" 
                header={th('paymentReports.columns.transactionDate')} 
                body={dateTemplate}
                sortable 
                style={{ width: '13%' }}
            />
            <Column 
                field="status" 
                header={th('paymentReports.columns.status')} 
                body={statusTemplate}
                sortable 
                style={{ width: '10%' }}
            />
        </DataTable>
    );
};

export default PaymentReportsTable;
